<template>
  <div class="members-reports-table">
    <div class="members-reports-table__header">
      <div class="members-reports-table__header-text">
        <h2 class="members-reports-table__title">Project members</h2>
        <p class="members-reports-table__subtitle">{{ countMembers }} DEVELOPERS</p>
      </div>
      <div
        v-if="currentMonth.status"
        class="members-reports-table__report-status"
        :class="{ 'members-reports-table__report-status--complete': isMonthComplete }"
      >
        {{ currentMonth.status }}
      </div>
    </div>

    <div class="members-reports-table__table">
      <v-data-table
        :headers="projectMembersReportsTable"
        :items="reportsWithIndex"
        :items-per-page="5"
        :show-arrows="true"
        :footer-props="{
          'items-per-page-options': [5, 10, 20],
          'next-icon': 'mdi-menu-right',
          'prev-icon': 'mdi-menu-left',
        }"
        dense
        no-data-text="No data"
        no-results-text="No data for that filters"
        class="dense-table"
      >
        <template #item.full_name="{ item }">
          <span class="members-reports-table__developer">
            <span class="members-reports-table__index">#{{ item.index }}</span>
            <router-link
              :to="{ name: 'hr-developer-show', params: { id: item.developer_id } }"
              class="members-reports-table__link"
            >
              <v-avatar
                size="32px"
                color="blue-light"
                class="members-reports-table__avatar"
              >
                <img
                  v-if="item.avatar"
                  alt="Avatar"
                  src="item.avatar"
                >
                <span v-else class="white--text">
                  {{ nameInitials(item.full_name) }}
                </span>
              </v-avatar>
              <span class="blue--text">{{ item.full_name }}</span>
            </router-link>
          </span>
        </template>

        <template #item.roles="{ value }">
          {{ value.join(', ') }}
        </template>

        <template #item.time_worked_in_seconds="{ value }">
          {{ formattedTime(value) }}
        </template>

        <template #item.rate="{ value }">
          {{ value }} PLN / h
        </template>

        <template #item.amount="{ value }">
          {{ value }} PLN
        </template>

        <template #item.status="{ value }">
          <div v-if="value" class="members-reports-table__status">
            {{ value }}
          </div>
        </template>

        <template #item.action="{ item }">
          <v-menu left min-width="100">
            <template v-slot:activator="{ on }">
              <v-btn icon color="secondary" v-on="on">
                <v-icon large>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>

            <v-list dense elevation-1>
              <v-list-item link
                :to="{ name: 'hr-developer-show', params: { id: item.developer_id } }"
              >
                <v-list-item-title>Profile</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openTimeEditModal(item)">
                <v-list-item-title>Add/edit time</v-list-item-title>
              </v-list-item>
              <v-list-item @click="showContractDetails(item.contract)">
                <v-list-item-title>Employment</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </div>

    <ContractDetailsModal
      v-model="isContractDetailsModalVisible"
      :current-contract="selectedContract"
    />

    <ReportTimeEditModal
      v-model="isReportTimeEditModalVisible"
      :report="selectedReport"
      :month="currentMonth.label"
      @change="updateData"
    />
  </div>
</template>

<script>
import { projectMembersReportsTable } from '@/helpers/tableHeaders';
import ReportTimeEditModal from '@/components/HrProjects/Finance/ReportTimeEditModal.vue';
import ContractDetailsModal from '@/components/ContractDetailsModal.vue';

export default {
  components: {
    ContractDetailsModal,
    ReportTimeEditModal,
  },

  props: {
    reports: {
      type: Array,
      required: true,
    },
    currentMonth: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      projectMembersReportsTable,
      isContractDetailsModalVisible: false,
      isReportTimeEditModalVisible: false,
      selectedContract: {},
      selectedReport: {},
      selectedMonth: '',
    };
  },

  computed: {
    countMembers() {
      return this.reports.length;
    },

    reportsWithIndex() {
      return this.reports.map(
        (reports, index) => ({
          ...reports,
          index: index + 1,
        }),
      );
    },

    isMonthComplete() {
      return this.currentMonth.status === 'Complete';
    },
  },

  methods: {
    nameInitials(name) {
      return name.split(' ').map((n) => n[0]).join('').substr(0, 2);
    },

    formattedTime(timeInSec) {
      let seconds = parseInt(timeInSec, 10);
      let hours = Math.floor(seconds / 3600);
      let minutes = Math.floor((seconds - (hours * 3600)) / 60);
      seconds = seconds - (hours * 3600) - (minutes * 60);
      if (hours < 10) hours = `0${hours}`;
      if (minutes < 10) minutes = `0${minutes}`;
      if (seconds < 10) seconds = `0${seconds}`;
      const time = `${hours}:${minutes}:${seconds}`;
      return time;
    },

    openTimeEditModal(report) {
      this.selectedReport = report;
      this.isReportTimeEditModalVisible = true;
    },

    showContractDetails(contract) {
      this.selectedContract = contract;
      this.isContractDetailsModalVisible = true;
    },

    updateData() {
      this.$emit('update');
    },
  },
};
</script>

<style lang="scss" scoped>
.members-reports-table {
  margin-bottom: 24px;
  padding: 16px 24px;
  background: #FFFFFF;
  box-shadow: 0px 3px 6px #00000029;
  border-radius: 8px;
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    justify-content: space-between;
  }

  &__title {
    font-size: 18px;
    font-weight: 500;
    line-height: 21px;
    color: $main--headers;
  }

  &__subtitle {
    font-size: 12px;
    font-weight: 500;
    line-height: 14px;
    color: $color--blue;
  }

  &__report-status {
    background: $color--yellow-light;
    color: $color--yellow;
    width: 102px;
    height: 32px;
    border-radius: 4px;
    text-align: center;
    line-height: 32px;
    font-size: 14px;

    &--complete {
      background: $color--green--light;
      color: $color--green;
    }
  }

  &__developer {
    display: flex;
    align-items: center;
    padding: 8px 0;
    font-weight: bold;
    color: $color--blue;
  }

  &__index {
    color: $main--text;
  }

  &__avatar {
    margin-right: 16px;
    margin-left: 16px;
  }

  &__link {
    text-decoration: unset !important;
  }

  &__status {
    font-size: 10px;
    background: $color--green--light;
    border-radius: 16px;
    color: $color--green;
    padding: 4px 16px;
    text-align: center;
  }
}
</style>
